import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { leaveOneSpace } from '@anecdotes/utils';
import { AbstractValueAccessor, MakeProvider } from '@anecdotes/design-system/form-controls';
import { MenuAction } from '../../types';
import { filter } from 'rxjs/operators';
import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Placement } from '@ng-bootstrap/ng-bootstrap';
import { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';

interface Calculation<TData> {
  searchValue?: string;
  data?: TData[];
  result?: TData[];
  scrollableItems?: TData[];
}

@Component({
  selector: 'app-multi-dropdown-control',
  templateUrl: './multi-dropdown-control.component.html',
  styleUrls: ['./multi-dropdown-control.component.scss'],
  providers: [MakeProvider(MultiDropdownControlComponent)],
})
export class MultiDropdownControlComponent<TData = any>
  extends AbstractValueAccessor
  implements OnInit, OnChanges, AfterViewInit {
  private calculation: Calculation<TData> = {};
  private openableElementElementRef: ElementRef<HTMLElement>;
  private previousVisibleItemsCount: number;

  @ViewChildren('openableElement', { read: ElementRef })
  private openableElementQueryList: QueryList<ElementRef<HTMLElement>>;

  @ViewChild('underlineSelectAllActionTemplate', { static: true })
  private underlineSelectAllActionRef: TemplateRef<any>;

  @ViewChild('trigger', { read: ElementRef })
  private triggerButton: ElementRef<HTMLElement>;

  @HostBinding('class.disabled')
  private get disabled(): boolean {
    return this.isDisabled || !this.data;
  }

  @ContentChild('dropdownTrigger')
  protected customTrigger: TemplateRef<any>;

  @ViewChild(CdkConnectedOverlay, { static: true })
  protected cdkConnectedOverlay: CdkConnectedOverlay;

  @HostBinding('class.has-value')
  get hasValue(): boolean {
    return !!this.value?.length;
  }

  readonly dropdownOptionHeight = 40;

  dictionary: Record<string, boolean> = {};
  initialPartiallyCheckedDictionary: Record<string, boolean> = {};
  partiallyCheckedDictionary: Record<string, boolean> = {};

  isDropdownOpened = false;
  searchField = new UntypedFormControl('');

  areAllSelected = false;
  selectAllUnderlineAction?: MenuAction;

  get anyItemSelected(): boolean {
    return Object.values(this.dictionary).find(Boolean);
  }

  get displayedData(): TData[] {
    return this.calculation.result;
  }

  get scrollableItems(): TData[] {
    return this.calculation.scrollableItems;
  }

  get listWidth(): number {
    if (!this.isDropdownOpened) {
      return 0;
    }

    switch (true) {
      case (this.customListWidth === 'fit-dropdown-button'):
        return this.hostElementRef.nativeElement.getBoundingClientRect().width;
      case (this.customListWidth === 'double-dropdown-button'):
        return this.hostElementRef.nativeElement.getBoundingClientRect().width * 2;
      case (typeof this.customListWidth === 'number'):
        return this.customListWidth as number;
      default:
        return;
    }
  }

  get listMaxHeight(): number {
    if (this.previousVisibleItemsCount !== this.visibleItemsCount) {
      const dropdownsHeight = this.visibleItemsCount * this.dropdownOptionHeight;
      const marginsHeight = this.visibleItemsCount ? (this.visibleItemsCount - 1) : 0;

      return dropdownsHeight + marginsHeight;
    }

    return this.previousVisibleItemsCount;
  }

  get selectedItems(): TData[] {
    return Object.entries(this.dictionary)
      .filter(([_, checked]) => !!checked)
      .map(([itemId]) => this.getItemById(itemId))
      .filter((itemId) => !!itemId);
  }

  get selectedItemsString(): string {
    const selectedCount = this.selectedItems.length;

    if (selectedCount === 0) {
      return '';
    }

    return this.selectedItems.map((item) => this.getDisplayValue(item)).join(`${this.valuesSeparatorCharacter} `);
  }

  get isBottomOptionShown(): boolean {
    if (!this.bottomDropdownAction) {
      return false;
    }

    if (this.bottomDropdownAction?.showOnlyOnSearch) {
      const searchValue = this.searchValue;

      if (this.bottomDropdownAction?.showOnlyForNotExistedValue) {
        return !!searchValue && !this.hasSearchEqualLabels(searchValue);
      }

      return !!searchValue;
    }

    if (this.bottomDropdownAction.displayCondition) {
      return this.bottomDropdownAction.displayCondition() as boolean;
    }

    return true;
  }

  get searchValue(): string {
    return leaveOneSpace(this.searchField.value);
  }

  get nextSelectAllCondition(): boolean {
    return !(this.selectAllStrategy === 'when-no-item-selected' ? this.anyItemSelected : this.areAllSelected);
  }

  get isSelectAllUnderlineAction(): boolean {
    return this.selectAllEnabled && this.selectAllPosition === 'underline-action';
  }

  get responsiveOverlayPositions(): ConnectedPosition[] {
    if (!this.triggerButton) {
      return;
    }

    const positionStartBottom: ConnectedPosition = {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    };
    const positionEndBottom: ConnectedPosition = {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    };
    const positionStartTop: ConnectedPosition = {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    };
    const positionEndTop: ConnectedPosition = {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    };

    this.cdkConnectedOverlay?.overlayRef?.updatePosition();

    return [positionStartBottom, positionEndBottom, positionStartTop, positionEndTop];
  }

  @Input()
  data: TData[];

  @Input()
  displaySelectedItemsList = true;

  // Used instead of displayValueSelector in checkbox text display
  @Input()
  displayValueTemplate?: TemplateRef<any>;

  @Input()
  displayValueSelector: (item: TData) => string;

  @Input()
  idSelector: (item: TData) => string;

  @Input()
  readOnlySelector: (item: TData) => boolean;

  @Input()
  context: any;

  @Input()
  loading: boolean;

  @Input()
  titleTranslationKey: string;

  @Input()
  titleInfoTooltip: string | TemplateRef<any>;

  @Input()
  customListWidth?: 'fit-dropdown-button' | 'double-dropdown-button' | 'responsive' | number = 'fit-dropdown-button';

  @Input()
  maxListWidthPx?: number;

  @Input()
  dropdownButtonViewTitle?: string;

  @Input()
  dropdownButtonType: 'dropdown-input' | 'button' = 'dropdown-input';

  @Input()
  placeholderTranslationKey: string;

  @Input()
  searchEnabled = false;

  @Input()
  searchAutoFocus = false;

  @Input()
  clearSearchOnDropdownClose = false;

  @Input()
  required: boolean;

  @Input()
  searchFieldPlaceholder: string;

  @Input()
  searchFieldWith: string;

  @Input()
  visibleItemsCount = 4;

  @Input()
  doesOptionShouldBeShownFully = false;

  @Input()
  valuesSeparatorCharacter = ';';

  @Input()
  selectAllEnabled = false;

  @Input()
  selectAllPosition: 'default' | 'underline-action' = 'default';

  @Input()
  titleInfoTooltipPlacement: Placement = 'top';

  @Input()
  selectAllCounterSelector?: (item: TData[]) => number;

  @Input()
  selectAllItemsSelector: (items: TData[]) => TData[];

  @Input()
  unSelectAllItemsSelector: (items: TData[]) => TData[];

  @Input()
  selectAllStrategy: 'default' | 'when-no-item-selected' = 'default';

  @Input()
  bottomDropdownAction: MenuAction;

  @Input()
  underlineActions: MenuAction[];

  @Input()
  dropdownPosition: ConnectedPosition[];

  @Input()
  firstOptionSticky = false;

  @Input()
  disabledFilterState? = false;

  @Input()
  hideEmptyListPlaceholder = false;

  @Input()
  enableBackdrop = false;

  @Input()
  controlTooltip?: string | TemplateRef<any>;

  @Input()
  controlTooltipPlacement: PlacementArray = 'right';

  @Output()
  changeCheckboxSelectionManually = new EventEmitter<{ itemId: string; checked: boolean }>();

  @Output()
  dropdownClose = new EventEmitter<any>();

  constructor(
    injector: Injector,
    protected translate: TranslateService,
    protected hostElementRef: ElementRef<HTMLElement>,
    protected cd: ChangeDetectorRef,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.searchEnabled) {
      this.searchField.valueChanges
        .pipe(
          filter(() => !!this.data),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((value: string) => {
          this.search(value);
          this.calculateDisplayedData();
        });
    }

    if (this.bottomDropdownAction?.shouldResetOnClick) {
      const action = this.bottomDropdownAction.action;
      this.bottomDropdownAction.action = (context) => this.handleBottomActionClick(action, context);
    }

    this.selectAllUnderlineAction = {
      translationKeyFactory: () => {
        return this.nextSelectAllCondition ? 'core.multiDropdown.selectAllWithCount' : 'core.multiDropdown.clearAll';
      },
      translationKeyParams: { count: this.getSelectAllCounter() },
      iconFactory: () => {
        return this.nextSelectAllCondition ? 'multiselect-checkbox-partial' : 'multiselect-checkbox-blue-70';
      },
      action: this.selectAll.bind(this),
    };
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    super.ngOnChanges(simpleChanges);

    if ('data' in simpleChanges) {
      if (this.selectAllUnderlineAction) {
        this.selectAllUnderlineAction.translationKeyParams = { count: this.getSelectAllCounter() };
      }
    }

    if ('data' in simpleChanges || 'firstOptionSticky' in simpleChanges) {
      this.calculateDisplayedData();
    }
  }

  ngAfterViewInit(): void {
    this.openableElementQueryList.notifyOnChanges();

    this.openableElementQueryList.changes
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.openableElementElementRef = this.openableElementQueryList.first));
  }

  menuActionTrackBy(index: number, menuAction: MenuAction): any {
    return menuAction?.id ? menuAction.id : menuAction;
  }

  writeValue(value: any[] | null, emitChanges?: boolean): void {
    const isArray = Array.isArray(value);

    if (isArray || value === null) {
      if (isArray) {
        const initialPartiallyCheckedDictionary = {};
        const partiallyCheckedDictionary = {};

        if (!value.length) {
          Object.keys(this.dictionary).forEach((key) => {
            this.dictionary[key] = false;
          });
        } else {
          const dictionary = {};

          value.forEach((item) => {
            const itemId = this.getItemId(item);

            if (item?.isPartial) {
              initialPartiallyCheckedDictionary[itemId] = true;
              partiallyCheckedDictionary[itemId] = true;
            } else {
              delete partiallyCheckedDictionary[itemId];
              dictionary[itemId] = true;
            }
          });

          this.dictionary = dictionary;
        }

        this.initialPartiallyCheckedDictionary = initialPartiallyCheckedDictionary;
        this.partiallyCheckedDictionary = partiallyCheckedDictionary;
      }

      if (emitChanges) {
        super.value = value;
      } else {
        super.writeValue(value);
      }

      this.updateSelectionFlag();

      this.cd.detectChanges();
    }
  }

  handleBottomActionClick(action: any, context: any): void {
    action(context);
    this.clearSearch();
  }

  search(searchValue: string): void {
    if (this.bottomDropdownAction?.showOnlyOnSearch) {
      this.bottomDropdownAction.translationKeyParams = { searchValue };
    }
  }

  getDisplayValue(item: TData): string {
    if (item) {
      return this.displayValueSelector ? this.displayValueSelector(item) : item.toString();
    }
  }

  getItemId(item: TData): string {
    if (item) {
      return this.idSelector ? this.idSelector(item) : item.toString();
    }
  }

  isItemReadOnly(item: TData): boolean {
    if (item) {
      return this.readOnlySelector ? this.readOnlySelector(item) : false;
    }
  }

  uncheckItem(item: TData): void {
    this.updateItem(item, false);
  }

  searchFieldClick($event: MouseEvent): void {
    $event.stopPropagation();
    $event.preventDefault();
  }

  toggleDropdown(): void {
    if (this.isDisabled$.value || this.loading) {
      return;
    }

    if (this.isDropdownOpened) {
      this.close();
    } else {
      this.open();
    }
  }

  checkboxValueLabel(item: TData): string {
    return this.displayValueTemplate ? undefined : this.getDisplayValue(item);
  }

  changeCheckboxManually(item: TData, event: { checked: boolean }): void {
    this.changeCheckboxSelectionManually.emit({
      itemId: this.getItemId(item),
      checked: event.checked,
    });
  }

  updateItem(item: TData, isChecked: boolean): void {
    const itemId = this.getItemId(item);

    if (this.initialPartiallyCheckedDictionary[itemId]) {
      switch (true) {
        /* Checked */
        case this.partiallyCheckedDictionary[itemId]: {
          this.partiallyCheckedDictionary[itemId] = false;
          this.dictionary[itemId] = true;
          break;
        }
        /* Unchecked */
        case this.dictionary[itemId]: {
          this.partiallyCheckedDictionary[itemId] = false;
          this.dictionary[itemId] = false;
          break;
        }
        /* Partial checked */
        case (!this.partiallyCheckedDictionary[itemId] && !this.dictionary[itemId]): {
          this.partiallyCheckedDictionary[itemId] = true;
          this.dictionary[itemId] = false;
          break;
        }
      }
    } else {
      this.dictionary[itemId] = isChecked;
    }

    this.value = [
      ...this.data.filter((item) => this.dictionary[this.getItemId(item)]),
      ...this.data
        .filter((item) => this.partiallyCheckedDictionary[this.getItemId(item)])
        .map((item) => ({ ...item, isPartial: true })),
    ];

    this.updateSelectionFlag();

    this.cd.detectChanges();
  }

  selectAll(): void {
    if (this.nextSelectAllCondition) {
      const data = this.selectAllItemsSelector ? this.selectAllItemsSelector(this.data) : this.data;
      this.writeValue(data, true);
    } else {
      this.unselectAll();
    }
  }

  unselectAll(): void {
    const data = this.unSelectAllItemsSelector ? this.unSelectAllItemsSelector(this.data) : [];
    this.writeValue(data, true);
  }

  hasSearchEqualLabels(searchValue: string): boolean {
    const hasEqualLabels = this.displayedData.some((item) => {
      const itemLabel = this.getDisplayValue(item);

      return itemLabel.toLocaleLowerCase() === searchValue.toLocaleLowerCase();
    });

    return hasEqualLabels;
  }

  close(): void {
    this.markAsDirty();

    this.dropdownClose.emit(this.value);
    this.isDropdownOpened = false;
    this.cd.detectChanges();

    if (this.initialPartiallyCheckedDictionary) {
      this.initialPartiallyCheckedDictionary = { ...this.partiallyCheckedDictionary };
    }

    if (this.clearSearchOnDropdownClose) {
      this.clearSearch();
    }
  }

  clearSearch(): void {
    this.searchField.setValue('');
    this.cd.detectChanges();
  }

  @HostListener('window:mousedown', ['$event'])
  private handleBackdropClick(mouseEvent: MouseEvent): void {
    const isClickedOutside = this.isDropdownOpened &&
      !mouseEvent
        .composedPath()
        .some((target) => target === this.hostElementRef.nativeElement || target === this.openableElementElementRef?.nativeElement);

    if (isClickedOutside) {
      this.close();
    }
  }

  private calculateDisplayedData(): void {
    const isSearchValueChanged = this.calculation.searchValue !== this.searchValue;
    const areDataChanged = JSON.stringify(this.calculation.data) !== JSON.stringify(this.data);

    if (areDataChanged || isSearchValueChanged) {
      this.calculation.result = this.data?.filter((item: TData) => {
        const itemLabel = this.getDisplayValue(item);

        if (!itemLabel) {
          return false;
        }

        const instant = this.translate.instant(itemLabel);
        let resolvedTranslation: string = itemLabel;

        if (instant && typeof instant === 'string') {
          resolvedTranslation = instant;
        }

        return resolvedTranslation.toLocaleLowerCase().includes(this.searchValue.toLocaleLowerCase());
      });
      this.calculation.scrollableItems = this.firstOptionSticky ? this.calculation.result.slice(1) : this.calculation.result;
      this.calculation.data = structuredClone(this.data);
      this.calculation.searchValue = this.searchValue;
    }
  }

  private updateSelectionFlag(): void {
    this.areAllSelected = this.data?.every((item) => this.dictionary[this.getItemId(item)]);
  }

  private open(): void {
    this.isDropdownOpened = true;
    this.cd.detectChanges();
  }

  private getItemById(id: string): TData {
    return this.data.find((item) => this.getItemId(item) === id);
  }

  private getSelectAllCounter(): number {
    if (this.selectAllCounterSelector) {
      return this.selectAllCounterSelector(this.data);
    }

    return this.data?.length;
  }
}
